import React from 'react'

import { twMerge } from 'tailwind-merge'

/**
 * Markdown component overrides for opportunity content (AI summaries, descriptions).
 * Provides consistent styling with moderate spacing.
 */
export const markdownComponents = {
  // Text elements
  p: ({ className, ...props }: React.HTMLProps<HTMLParagraphElement>) => (
    <p
      className={twMerge(
        'my-3 first:mt-0 last:mb-0 leading-relaxed',
        className,
      )}
      {...props}
    />
  ),
  strong: ({ className, ...props }: React.HTMLProps<HTMLElement>) => (
    <strong className={twMerge('font-semibold', className)} {...props} />
  ),

  // Lists
  ul: ({ className, ...props }: React.HTMLProps<HTMLUListElement>) => (
    <ul
      className={twMerge(
        'my-3 first:mt-0 last:mb-0 pl-0 space-y-1.5 list-none',
        className,
      )}
      {...props}
    />
  ),
  ol: ({ className, ...props }: React.HTMLProps<HTMLOListElement>) => {
    const { type, ...restProps } = props
    return (
      <ol
        className={twMerge(
          'my-3 first:mt-0 last:mb-0 pl-0 space-y-1.5 list-none counter-reset-[item]',
          className,
        )}
        {...restProps}
      />
    )
  },
  li: ({ className, children, ...props }: React.HTMLProps<HTMLLIElement>) => (
    <li
      className={twMerge(
        'relative pl-5 before:absolute before:left-0 before:top-[0.5em] before:w-1.5 before:h-1.5 before:rounded-full before:bg-accent-500 dark:before:bg-accent-400',
        className,
      )}
      {...props}
    >
      {children}
    </li>
  ),

  // Headings - rendered as styled paragraphs in this context
  h1: ({
    className,
    children,
    ...props
  }: React.HTMLProps<HTMLHeadingElement>) => (
    <p
      className={twMerge('text-base font-bold mt-4 mb-2 first:mt-0', className)}
      {...props}
    >
      {children}
    </p>
  ),
  h2: ({
    className,
    children,
    ...props
  }: React.HTMLProps<HTMLHeadingElement>) => (
    <p
      className={twMerge('text-base font-bold mt-4 mb-2 first:mt-0', className)}
      {...props}
    >
      {children}
    </p>
  ),
  h3: ({
    className,
    children,
    ...props
  }: React.HTMLProps<HTMLHeadingElement>) => (
    <p
      className={twMerge('font-semibold mt-3 mb-1.5 first:mt-0', className)}
      {...props}
    >
      {children}
    </p>
  ),
  h4: ({
    className,
    children,
    ...props
  }: React.HTMLProps<HTMLHeadingElement>) => (
    <p
      className={twMerge('font-semibold mt-3 mb-1.5 first:mt-0', className)}
      {...props}
    >
      {children}
    </p>
  ),

  // Links
  a: ({ className, ...props }: React.HTMLProps<HTMLAnchorElement>) => (
    <a
      className={twMerge(
        'text-accent-600 dark:text-accent-400 hover:underline underline-offset-2',
        className,
      )}
      {...props}
    />
  ),

  // Code
  code: ({ className, ...props }: React.HTMLProps<HTMLElement>) => (
    <code
      className={twMerge(
        'px-1 py-0.5 rounded bg-primary-100 dark:bg-primary-800 text-[0.9em] font-mono',
        className,
      )}
      {...props}
    />
  ),
  pre: ({ className, ...props }: React.HTMLProps<HTMLPreElement>) => (
    <pre
      className={twMerge(
        'my-3 first:mt-0 last:mb-0 p-3 rounded-lg bg-primary-100 dark:bg-primary-800 overflow-x-auto text-sm font-mono',
        className,
      )}
      {...props}
    />
  ),

  // Blockquote
  blockquote: ({ className, ...props }: React.HTMLProps<HTMLQuoteElement>) => (
    <blockquote
      className={twMerge(
        'my-3 first:mt-0 last:mb-0 pl-4 border-l-2 border-primary-300 dark:border-primary-600 italic [&>p]:my-1',
        className,
      )}
      {...props}
    />
  ),

  // HR
  hr: ({ className, ...props }: React.HTMLProps<HTMLHRElement>) => (
    <hr
      className={twMerge(
        'my-4 border-0 h-px bg-primary-200 dark:bg-primary-700',
        className,
      )}
      {...props}
    />
  ),
}

/**
 * Compact markdown components for inline/embedded contexts like notice descriptions.
 * Uses tighter spacing appropriate for small text blocks.
 */
export const compactMarkdownComponents = {
  // Text elements - tight spacing
  p: ({ className, ...props }: React.HTMLProps<HTMLParagraphElement>) => (
    <p className={twMerge('my-1 first:mt-0 last:mb-0', className)} {...props} />
  ),
  strong: ({ className, ...props }: React.HTMLProps<HTMLElement>) => (
    <strong className={twMerge('font-semibold', className)} {...props} />
  ),

  // Lists - compact
  ul: ({ className, ...props }: React.HTMLProps<HTMLUListElement>) => (
    <ul
      className={twMerge(
        'my-1 first:mt-0 last:mb-0 pl-0 space-y-0 list-none',
        className,
      )}
      {...props}
    />
  ),
  ol: ({ className, ...props }: React.HTMLProps<HTMLOListElement>) => {
    const { type, ...restProps } = props
    return (
      <ol
        className={twMerge(
          'my-1 first:mt-0 last:mb-0 pl-0 space-y-0 list-none counter-reset-[item]',
          className,
        )}
        {...restProps}
      />
    )
  },
  li: ({ className, children, ...props }: React.HTMLProps<HTMLLIElement>) => (
    <li
      className={twMerge(
        'relative pl-3.5 before:absolute before:left-0 before:top-[0.45em] before:w-1 before:h-1 before:rounded-full before:bg-primary-400 dark:before:bg-primary-500',
        className,
      )}
      {...props}
    >
      {children}
    </li>
  ),

  // Headings - minimal in compact context
  h1: ({
    className,
    children,
    ...props
  }: React.HTMLProps<HTMLHeadingElement>) => (
    <p
      className={twMerge('font-bold mt-1.5 mb-0.5 first:mt-0', className)}
      {...props}
    >
      {children}
    </p>
  ),
  h2: ({
    className,
    children,
    ...props
  }: React.HTMLProps<HTMLHeadingElement>) => (
    <p
      className={twMerge('font-bold mt-1.5 mb-0.5 first:mt-0', className)}
      {...props}
    >
      {children}
    </p>
  ),
  h3: ({
    className,
    children,
    ...props
  }: React.HTMLProps<HTMLHeadingElement>) => (
    <p
      className={twMerge('font-semibold mt-1 mb-0.5 first:mt-0', className)}
      {...props}
    >
      {children}
    </p>
  ),
  h4: ({
    className,
    children,
    ...props
  }: React.HTMLProps<HTMLHeadingElement>) => (
    <p
      className={twMerge('font-semibold mt-1 mb-0.5 first:mt-0', className)}
      {...props}
    >
      {children}
    </p>
  ),

  // Links
  a: ({ className, ...props }: React.HTMLProps<HTMLAnchorElement>) => (
    <a
      className={twMerge(
        'text-accent-600 dark:text-accent-400 hover:underline underline-offset-2',
        className,
      )}
      {...props}
    />
  ),

  // Code
  code: ({ className, ...props }: React.HTMLProps<HTMLElement>) => (
    <code
      className={twMerge(
        'px-1 py-0.5 rounded bg-primary-100 dark:bg-primary-800 text-[0.9em] font-mono',
        className,
      )}
      {...props}
    />
  ),

  // Blockquote - compact
  blockquote: ({ className, ...props }: React.HTMLProps<HTMLQuoteElement>) => (
    <blockquote
      className={twMerge(
        'my-1 first:mt-0 last:mb-0 pl-2.5 border-l-2 border-primary-300 dark:border-primary-600 italic [&>p]:my-0',
        className,
      )}
      {...props}
    />
  ),

  // HR - compact
  hr: ({ className, ...props }: React.HTMLProps<HTMLHRElement>) => (
    <hr
      className={twMerge(
        'my-1.5 border-0 h-px bg-primary-200 dark:bg-primary-700',
        className,
      )}
      {...props}
    />
  ),
}
